'use client'

import React, { useState, useMemo } from 'react'
import { mutate } from 'swr'
import { useApi } from '@excellent/ui/api'
import { apiCall, apiKey, ApiError } from '@excellent/ui/api'
import { lens } from '@excellent/ui/api/contracts/lens'
import { Badge, Button, Card, IntentBadge, PageHeader, SearchInput, Skeleton, StatGrid, StatStrip, Textarea } from "@excellent/ui"
import type { Intent, StatStripItem } from "@excellent/ui"
import type { MemoryEntry, MemoryType } from '@lens/lib/claude-reader'
import { projectDisplayName, projectShortPath, formatRelativeDate } from '@lens/lib/decode'

// ── Type config ───────────────────────────────────────────────────────────────
//
// Memory types render through the canonical <IntentBadge> pill. The `dot`
// color (a distinct chart hue per type) is kept only for the card's accent
// rail/border — the tag pill itself uses the shared intent palette so it
// matches every other status/kind tag across the app.

const TYPE_META: Record<MemoryType, { label: string; intent: Intent; dot: string }> = {
 user: { label: 'user', intent: 'info', dot: 'var(--viz-sky)' },
 feedback: { label: 'feedback', intent: 'danger', dot: 'var(--destructive)' },
 project: { label: 'project', intent: 'success', dot: 'var(--chart-5)' },
 reference: { label: 'reference', intent: 'neutral', dot: 'var(--chart-1)' },
 index: { label: 'index', intent: 'warning', dot: 'var(--chart-4)' },
 unknown: { label: '?', intent: 'neutral', dot: 'var(--foreground)' },
}

const FILTER_TYPES = ['all', 'user', 'feedback', 'project', 'reference', 'index'] as const
type FilterType = typeof FILTER_TYPES[number]

function TypeBadge({ type }: { type: MemoryType }) {
 const m = TYPE_META[type] ?? TYPE_META.unknown
 return <IntentBadge intent={m.intent}>{m.label}</IntentBadge>
}

function StaleBadge({ mtime }: { mtime: string }) {
 // eslint-disable-next-line react-hooks/purity
 const daysOld = Math.floor((Date.now() - new Date(mtime).getTime()) / 86_400_000)
 if (daysOld < 30) return null
 return <IntentBadge intent="danger">stale</IntentBadge>
}

// ── Memory card ───────────────────────────────────────────────────────────────

function MemoryCard({ entry, onClick, expanded }: { entry: MemoryEntry; onClick: () => void; expanded: boolean }) {
 const projectName = projectDisplayName(entry.projectPath)
 const shortPath = projectShortPath(entry.projectPath)
 const m = TYPE_META[entry.type] ?? TYPE_META.unknown
 const preview = entry.body.slice(0, 200).replace(/\n+/g, ' ').trim()

 const [editing, setEditing] = useState(false)
 const [draft, setDraft] = useState(entry.body)
 const [saving, setSaving] = useState(false)
 const [saveError, setSaveError] = useState<string | null>(null)

 async function handleSave(e: React.MouseEvent) {
 e.stopPropagation()
 setSaving(true)
 setSaveError(null)
 try {
 await apiCall(lens.memoryPatch, {
 body: {
 projectSlug: entry.projectSlug,
 file: entry.file,
 content: draft,
 },
 })
 setEditing(false)
 mutate(apiKey(lens.memoryList, {}))
 } catch (err) {
 setSaveError(err instanceof ApiError ? err.error : String(err))
 } finally {
 setSaving(false)
 }
 }

 function handleEdit(e: React.MouseEvent) {
 e.stopPropagation()
 setDraft(entry.body)
 setSaveError(null)
 setEditing(true)
 }

 function handleCancel(e: React.MouseEvent) {
 e.stopPropagation()
 setEditing(false)
 setSaveError(null)
 setDraft(entry.body)
 }

 return (
 <Card
 className={[
 'gap-0 py-0 transition-all',
 editing ? 'cursor-default' : 'cursor-pointer',
 expanded ? '' : 'hover:border-primary/30',
 ].join(' ')}
 onClick={editing ? undefined : onClick}
 style={expanded ? { borderColor: m.dot + '66' } : undefined}
 >
 <div className="px-4 py-3.5 flex items-start gap-3">
 {/* Type dot */}
 <div
 className="flex-shrink-0 w-2 h-2 rounded-lg mt-2"
 style={{ backgroundColor: m.dot }}
 />

 <div className="flex-1 min-w-0 space-y-1.5">
 {/* Header row */}
 <div className="flex flex-wrap items-center gap-2">
 <span className="type-body text-foreground truncate">{entry.name}</span>
 <TypeBadge type={entry.type} />
 <StaleBadge mtime={entry.mtime} />
 {expanded && !editing && (
 <Button
 variant="outline"
 size="xs"
 onClick={handleEdit}
 className="ml-auto"
 >
 edit
 </Button>
 )}
 </div>

 {/* Description */}
 {entry.description && (
 <p className="type-body text-muted-foreground">{entry.description}</p>
 )}

 {/* Body preview (collapsed) */}
 {!expanded && preview && (
 <p className="type-body text-muted-foreground line-clamp-2">{preview}</p>
 )}

 {/* Full body (expanded, read mode) */}
 {expanded && !editing && (
 <pre className="mt-2 type-body text-foreground whitespace-pre-wrap bg-muted/40 rounded p-3 overflow-x-auto max-h-96 overflow-y-auto">
 {entry.body}
 </pre>
 )}

 {/* Edit mode */}
 {expanded && editing && (
 <div className="mt-2 space-y-2" onClick={e => e.stopPropagation()}>
 <Textarea
 className="min-h-64 resize-y"
 value={draft}
 onChange={e => setDraft(e.target.value)}
 spellCheck={false}
 />
 {saveError && (
 <p className="type-body text-destructive">{saveError}</p>
 )}
 <div className="flex gap-2">
 <Button
 size="sm"
 onClick={handleSave}
 disabled={saving}
 >
 {saving ? 'saving…' : 'save'}
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={handleCancel}
 disabled={saving}
 >
 cancel
 </Button>
 </div>
 </div>
 )}

 {/* Footer */}
 <div className="flex flex-wrap items-center gap-2 pt-0.5">
 <Badge variant="outline">{projectName}</Badge>
 <span className="type-body text-muted-foreground">{shortPath}</span>
 <span className="type-body text-muted-foreground ml-auto">
 {formatRelativeDate(entry.mtime)}
 </span>
 </div>
 </div>
 </div>
 </Card>
 )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MemoryPage() {
 const { data, error, isLoading } = useApi(lens.memoryList, {}) as { data: { memories: MemoryEntry[] } | undefined; error: unknown; isLoading: boolean }
 const [filter, setFilter] = useState<FilterType>('all')
 const [search, setSearch] = useState('')
 const [expandedId, setExpandedId] = useState<string | null>(null)

 const memories = data?.memories ?? []

 const counts = useMemo(() => {
 const c: Record<string, number> = { all: memories.length }
 for (const type of ['user', 'feedback', 'project', 'reference', 'index']) {
 c[type] = memories.filter(m => m.type === type).length
 }
 return c
 }, [memories])

 const staleCount = useMemo(
 // eslint-disable-next-line react-hooks/purity
 () => memories.filter(m => (Date.now() - new Date(m.mtime).getTime()) / 86_400_000 >= 30).length,
 [memories]
 )

 const projectCount = useMemo(
 () => new Set(memories.map(m => m.projectSlug)).size,
 [memories]
 )

 const filtered = useMemo(() => {
 return memories.filter(m => {
 if (filter !== 'all' && m.type !== filter) return false
 if (search) {
 const q = search.toLowerCase()
 return (
 m.name.toLowerCase().includes(q) ||
 m.description.toLowerCase().includes(q) ||
 m.body.toLowerCase().includes(q) ||
 m.projectPath.toLowerCase().includes(q)
 )
 }
 return true
 })
 }, [memories, filter, search])

 function toggleExpand(id: string) {
 setExpandedId(prev => (prev === id ? null : id))
 }

 const memorySubtitle = data && (search || filter !== 'all')
 ? `${filtered.length.toLocaleString()} of ${memories.length.toLocaleString()} memories`
 : undefined

 const memoryStats: StatStripItem[] = useMemo(() => [
 { title: 'Total Memories', value: memories.length.toLocaleString(), accentColor: 'var(--chart-4)' },
 { title: 'Projects', value: projectCount.toLocaleString(), accentColor: 'var(--viz-sky)' },
 { title: 'Feedback', value: (counts.feedback ?? 0).toLocaleString(), accentColor: 'var(--destructive)' },
 { title: 'Stale (>30d)', value: staleCount.toLocaleString(), accentColor: 'var(--foreground)' },
 ], [memories.length, projectCount, counts.feedback, staleCount])

 return (
 <div className="flex flex-col">
 <div className="space-y-5">

 <PageHeader
 title="Memory"
 description={memorySubtitle}
 toolbar={
 data ? (
 <SearchInput
 value={search}
 onChange={e => setSearch(e.target.value)}
 placeholder="Search memories…"
 />
 ) : undefined
 }
 />

 {error ? <p className="text-destructive type-body ">Error loading memories.</p> : null}

 {isLoading && (
 <div className="space-y-3">
 <StatGrid cols={4}>
 {Array.from({ length: 4 }).map((_, i) => (
 <Skeleton key={i} className="h-20 rounded-lg" />
 ))}
 </StatGrid>
 {Array.from({ length: 6 }).map((_, i) => (
 <Skeleton key={i} className="h-24 rounded-lg" />
 ))}
 </div>
 )}

 {data && (
 <>
 {/* Stat strip */}
 <StatStrip variant="standalone" items={memoryStats} />

 {/* Type filter tabs */}
 <div className="flex flex-wrap gap-2">
 {FILTER_TYPES.map(type => {
 const m = type === 'all' ? null : TYPE_META[type as MemoryType]
 const count = counts[type] ?? 0
 const active = filter === type
 return (
 // button-ignore: segmented type-filter control (custom dot + tinted active), not an action button
 <button
 key={type}
 onClick={() => setFilter(type)}
 className={[
 'flex items-center gap-1.5 px-3 py-1.5 rounded border type-body transition-all',
 active
 ? 'border-primary/50 bg-primary/10 text-foreground'
 : 'border-border bg-card text-foreground hover:border-primary/30 hover:text-foreground',
 ].join(' ')}
 >
 {m && (
 <span className="w-1.5 h-1.5 rounded-lg flex-shrink-0" style={{ backgroundColor: m.dot }} />
 )}
 {type}
 <span className="opacity-60">({count})</span>
 </button>
 )
 })}
 </div>

 {/* Memory list */}
 {filtered.length === 0 ? (
 <div className="text-center py-16">
 <p className="text-foreground type-body">
 {memories.length === 0
 ? 'No memory files found in ~/.claude/projects/*/memory/'
 : 'No memories match your filter.'}
 </p>
 </div>
 ) : (
 <div className="space-y-2">
 {filtered.map(entry => {
 const id = `${entry.projectSlug}/${entry.file}`
 return (
 <MemoryCard
 key={id}
 entry={entry}
 expanded={expandedId === id}
 onClick={() => toggleExpand(id)}
 />
 )
 })}
 </div>
 )}
 </>
 )}
 </div>
 </div>
 )
}
